/**
 * Legacy chip URL state — parse the old ``?chips=`` param and convert it once
 * into keyword groups (``chipsToGroups``); chips are no longer written.
 *
 * Wire shape: a compact JSON array ``[{k, v, p?}]`` — ``k`` = kind, ``v`` =
 * value, ``p`` omitted entirely when false/absent (spec: "p omitted when
 * false").
 *
 * ``parseChips`` absorbs malformed input rather than throwing: bad JSON, a
 * non-array, a non-object entry, an unknown ``kind`` or a missing/empty
 * ``value`` all fail the WHOLE parse closed to ``[]`` (test plan §1.4 /
 * F5) — a partially-valid array is not partially trusted.
 */
import type { Chip, ChipKind, KeywordSearchGroup, KeywordSearchRow } from '../types';
import { patternKeyword } from '../utils/keywordMatchRule';

export type { Chip, ChipKind };

const VALID_KINDS: ReadonlySet<string> = new Set<ChipKind>([
  'tag',
  'generic',
  'brand',
  'manufacturer',
  'application',
]);

/** Parse the ``chips`` URL param back into ``Chip[]``. Never throws — any
 *  malformed shape (bad JSON, non-array, bad entry) resolves to ``[]``. */
export function parseChips(raw: string | null): Chip[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const result: Chip[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== 'object') return [];
    const kind = (entry as Record<string, unknown>)['k'];
    const value = (entry as Record<string, unknown>)['v'];
    if (typeof kind !== 'string' || !VALID_KINDS.has(kind)) return [];
    if (typeof value !== 'string' || value.length === 0) return [];
    const chip: Chip = { kind: kind as ChipKind, value };
    if ((entry as Record<string, unknown>)['p'] === true) chip.pattern = true;
    result.push(chip);
  }
  return result;
}

const MAX_ROWS_PER_FACET = 10;

/** Convert legacy chips to keyword groups: one group per facet, in the order
 *  tag (AND inside), name = brand + generic (OR), manufacturer (OR),
 *  application (OR); groups joined by AND. A facet keeps its first 10 rows. */
export function chipsToGroups(chips: Chip[]): { groups: KeywordSearchGroup[]; truncated: boolean } {
  const facets: Array<{ kinds: ChipKind[]; op: 'AND' | 'OR' }> = [
    { kinds: ['tag'], op: 'AND' },
    { kinds: ['brand', 'generic'], op: 'OR' },
    { kinds: ['manufacturer'], op: 'OR' },
    { kinds: ['application'], op: 'OR' },
  ];
  const groups: KeywordSearchGroup[] = [];
  let truncated = false;
  for (const { kinds, op } of facets) {
    const mine = chips.filter((c) => kinds.includes(c.kind));
    if (mine.length === 0) continue;
    if (mine.length > MAX_ROWS_PER_FACET) truncated = true;
    const rows: KeywordSearchRow[] = mine.slice(0, MAX_ROWS_PER_FACET).map((c) => ({
      // A pattern means "contains": carry the ``*`` the matching rule uses.
      keyword: c.pattern ? patternKeyword(c.value) : c.value,
      section_scope: null,
      match_type: 'exact',
      use_synonyms: false,
      custom_synonyms: null,
      negate: false,
      drug_field: c.kind,
    }));
    groups.push({ rows, inner_operator: op, preceding_operator: 'AND' });
  }
  return { groups, truncated };
}
